import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ChevronDown, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { useDianaUIPortal } from './provider/DianaUIProvider.js';
import { Button, IconButton } from './primitives.js';

export function Dialog({
  trigger,
  title,
  description,
  children,
  destructive,
  eyebrow,
}: {
  trigger: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
  destructive?: boolean;
  eyebrow?: string;
}) {
  const portal = useDianaUIPortal();
  return (
    <DialogPrimitive.Root>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal container={portal}>
        <DialogPrimitive.Overlay className="dui-dialog-overlay" />
        <DialogPrimitive.Content className="dui-dialog">
          <div>
            {eyebrow || destructive ? (
              <p className="dui-dialog__eyebrow">{eyebrow ?? 'Permanent action'}</p>
            ) : null}
            <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description>{description}</DialogPrimitive.Description>
            ) : null}
          </div>
          {children}
          <div className="dui-dialog__actions">
            <DialogPrimitive.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogPrimitive.Close>
            <DialogPrimitive.Close asChild>
              <Button variant={destructive ? 'destructive' : 'primary'}>
                {destructive ? 'Delete item' : 'Confirm'}
              </Button>
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Close asChild>
            <IconButton
              className="dui-dialog__close"
              icon={X}
              label="Close dialog"
              variant="quiet"
              size="small"
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Menu({
  triggerLabel,
  items,
}: {
  triggerLabel: string;
  items: readonly { label: string; disabled?: boolean; destructive?: boolean }[];
}) {
  const portal = useDianaUIPortal();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button trailingIcon={ChevronDown}>{triggerLabel}</Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal container={portal}>
        <DropdownMenu.Content className="dui-menu" sideOffset={6}>
          {items.map((item) => (
            <DropdownMenu.Item
              key={item.label}
              className="dui-menu__item"
              data-destructive={item.destructive || undefined}
              disabled={item.disabled}
            >
              {item.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function Popover({
  trigger,
  title,
  children,
  open,
  onOpenChange,
}: {
  trigger: ReactNode;
  title: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?(open: boolean): void;
}) {
  const portal = useDianaUIPortal();
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={portal}>
        <PopoverPrimitive.Content className="dui-popover" sideOffset={8}>
          <strong>{title}</strong>
          {children}
          <PopoverPrimitive.Arrow className="dui-popover__arrow" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  const portal = useDianaUIPortal();
  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={portal}>
          <TooltipPrimitive.Content className="dui-tooltip" sideOffset={6}>
            {label}
            <TooltipPrimitive.Arrow className="dui-tooltip__arrow" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

type SheetProps = {
  trigger: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  open?: boolean;
  onOpenChange?(open: boolean): void;
};

function Sheet({
  trigger,
  title,
  description,
  children,
  footer,
  open,
  onOpenChange,
  variant,
}: SheetProps & { variant: 'drawer' | 'bottom-sheet' }) {
  const portal = useDianaUIPortal();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal container={portal}>
        <DialogPrimitive.Overlay className="dui-dialog-overlay" />
        <DialogPrimitive.Content
          className={`dui-sheet dui-sheet--${variant}`}
          {...(!description ? { 'aria-describedby': undefined } : {})}
        >
          {variant === 'bottom-sheet' ? (
            <div className="dui-sheet__grabber" aria-hidden="true" />
          ) : null}
          <header className="dui-sheet__header">
            <div>
              <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description>{description}</DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild>
              <IconButton
                icon={X}
                label={variant === 'drawer' ? 'Close drawer' : 'Close sheet'}
                variant="quiet"
              />
            </DialogPrimitive.Close>
          </header>
          <div className="dui-sheet__body">{children}</div>
          {footer ? <footer className="dui-sheet__footer">{footer}</footer> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Drawer(props: SheetProps) {
  return <Sheet {...props} variant="drawer" />;
}
export function BottomSheet(props: SheetProps) {
  return <Sheet {...props} variant="bottom-sheet" />;
}
